<template>
    <transition name="modal-fade">
        <div v-if="isOpen" class="modal-overlay" @click.self="close">
            <div class="modal-card">
                
                <div class="modal-header">
                    <h5 class="modal-title">{{ title || 'Selecione uma opção' }}</h5>
                    <button type="button" class="btn-close-modal" @click="close">
                        <i class="ri-close-line"></i>
                    </button>
                </div>

                <div class="modal-body custom-scrollbar">
                    <div class="options-list">
                        <label 
                            v-for="option in options" 
                            :key="option.id" 
                            class="option-item"
                            :class="{ 'selected': selectedOptionId === option.id }"
                        >
                            <input 
                                type="radio" 
                                name="list-option" 
                                :value="option.id" 
                                v-model="selectedOptionId"
                                class="d-none"
                            >
                            <div class="radio-circle">
                                <div class="radio-dot"></div>
                            </div>
                            <span class="option-text">{{ option.text }}</span>
                        </label>
                    </div>
                </div>

                <div class="modal-footer">
                    <button 
                        class="btn-send-selection" 
                        :disabled="!selectedOptionId"
                        @click="handleSend"
                    >
                        Enviar
                    </button>
                </div>

            </div>
        </div>
    </transition>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useUiStore } from '@/stores/app/uiStore';
import { useChatStore } from '@/stores/app/chatStore';

const uiStore = useUiStore();
const chatStore = useChatStore();

const isOpen = computed(() => uiStore.isListMessageModalOpen);
const listData = computed(() => uiStore.listMessageData); // { title: '', options: [], messageId: ... }

const title = computed(() => listData.value?.title || 'Selecione uma opção');
const options = computed(() => listData.value?.options || []);

const selectedOptionId = ref(null);

watch(isOpen, (val) => {
    if (val) selectedOptionId.value = null;
});

const close = () => {
    uiStore.closeListMessageModal();
};

const handleSend = () => {
    if (!selectedOptionId.value) return;

    const selectedOption = options.value.find(o => o.id === selectedOptionId.value);
    
    if (selectedOption) {
        chatStore.sendMessage(selectedOption.text);
    }
    close();
};
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0; left: 0; width: 100%; height: 100%;
    z-index: 1050;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(2px);
}

.modal-card {
    background-color: var(--bs-white);
    width: 90%;
    max-width: 450px;
    border-radius: 16px; 
    box-shadow: 0 17px 50px 0 rgba(11,20,26,.19), 0 12px 15px 0 rgba(11,20,26,.24);
    display: flex;
    flex-direction: column;
    max-height: 80vh;
    overflow: hidden;
    animation: zoomIn 0.2s cubic-bezier(0.1, 0.82, 0.25, 1);
}

/* === HEADER === */
.modal-header {
    padding: 18px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: var(--bs-light); 
    border-bottom: 1px solid rgba(0,0,0,0.06);
}

.modal-title {
    font-size: 19px;
    font-weight: 600;
    color: var(--bs-body-color);
    margin: 0;
}

.btn-close-modal {
    background: transparent;
    border: none;
    font-size: 24px;
    color: var(--bs-secondary-color);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: color 0.2s;
    width: 32px; height: 32px;
    border-radius: 50%;
}
.btn-close-modal:hover {
    background-color: rgba(0,0,0,0.05);
    color: var(--bs-body-color);
}

.modal-body {
    padding: 8px 0;
    overflow-y: auto;
}

.option-item {
    display: flex;
    align-items: center;
    padding: 14px 24px;
    cursor: pointer;
    transition: background-color 0.15s ease;
    gap: 16px;
    user-select: none;
    position: relative;
}

.option-item:hover {
    background-color: #f5f6f6;
}

.radio-circle {
    width: 20px;
    height: 20px;
    border: 2px solid #8696a0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
}

.radio-dot {
    width: 10px;
    height: 10px;
    background-color: transparent;
    border-radius: 50%;
    transition: background-color 0.2s ease;
}

.option-item.selected .radio-circle {
    border-color: #00a884;
    background-color: transparent;
}
.option-item.selected .radio-dot {
    background-color: #00a884;
}
.option-item.selected {
    background-color: rgba(0, 168, 132, 0.04); 
}

.option-text {
    font-size: 16px;
    color: var(--bs-body-color);
    font-weight: 400;
}

.modal-footer {
    padding: 16px 24px;
    border-top: 1px solid rgba(0,0,0,0.06);
    display: flex;
    justify-content: flex-end;
    background-color: var(--bs-white);
}

.btn-send-selection {
    background-color: #00a884;
    color: #fff;
    border: none;
    padding: 10px 24px;
    border-radius: 24px; 
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.btn-send-selection:hover:not(:disabled) {
    background-color: #008f6f;
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.btn-send-selection:disabled {
    background-color: #e9edef;
    color: #aebac1;
    cursor: not-allowed;
    box-shadow: none;
}

[data-bs-theme="dark"] .btn-close-modal:hover {
    background-color: rgba(255,255,255,0.08);
}

[data-bs-theme="dark"] .option-item:hover {
    background-color: var(--bs-light); 
}
[data-bs-theme="dark"] .radio-circle {
    border-color: #aebac1;
}
[data-bs-theme="dark"] .option-item.selected {
    background-color: rgba(0, 168, 132, 0.08);
}

[data-bs-theme="dark"] .btn-send-selection:disabled {
    background-color: #3b4a54;
    color: #8696a0;
}

@keyframes zoomIn {
    from { opacity: 0; transform: scale(0.9); }
    to { opacity: 1; transform: scale(1); }
}

.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.2s; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }
</style>